import { FC } from 'react';
import { Box, Chip, Stack, Theme, Typography } from '@mui/material';

import { LocalizationTypography } from '~components/organisms/localization';
import { useLanguageContext } from '~providers';
import { UserPassState } from '~types/subscription.type';
import { getRemainingTime } from '~utils/date.util';

const GRADIENTS = {
  active: (theme: Theme) =>
    `linear-gradient(90deg, #6366F1 0%, ${theme.palette.brand.main} 100%)`,
  inactive: 'linear-gradient(90deg, #9CA3AF 0%, #4B5563 100%)',
  // Warm stone against the cool slate of `inactive`: a cancelled pass is closed
  // and inert, a pending renewal is still live and waiting on something. Warm
  // versus cool separates them at a glance without either card raising its
  // voice, and it is the ground the amber chip was picked for.
  problem: 'linear-gradient(90deg, #B0A79D 0%, #6B6259 100%)',
};

const PASS_STATE_LABELS: Record<UserPassState, string> = {
  [UserPassState.ACTIVE]: 'Active',
  [UserPassState.CANCELED]: 'Cancelled',
  [UserPassState.ENDED]: 'Ended',
};

const CHIP_COLORS: Record<UserPassState, string> = {
  [UserPassState.ACTIVE]: '#16A34A',
  [UserPassState.CANCELED]: '#EF4444',
  [UserPassState.ENDED]: '#6B7280',
};

export const PassStateChip: FC<{ state: UserPassState }> = ({ state }) => (
  <Chip
    size="small"
    label={
      <LocalizationTypography
        textId={`app.rentalPage.newStepThree.pass.state.${state.toLowerCase()}`}
        defaultText={PASS_STATE_LABELS[state]}
        variant="Poppins/Subheading-sm"
        color="#FFFFFF"
      />
    }
    sx={{
      backgroundColor: CHIP_COLORS[state],
      borderRadius: 50,
      height: 'auto',
      py: 0.5,
      '& .MuiChip-label': { px: 1.5 },
    }}
  />
);

/**
 * Drops to the next unit down as the pass runs out, so an hourly pass never
 * spends its whole life reading "0d 0h".
 */
const formatRemaining = (remaining: {
  days: number;
  hours: number;
  minutes: number;
}): string => {
  if (remaining.days > 0) {
    return `${remaining.days}d ${remaining.hours}h`;
  }

  if (remaining.hours > 0) {
    return `${remaining.hours}h ${remaining.minutes}m`;
  }

  return `${remaining.minutes}m`;
};

const RetryingChip: FC = () => (
  <Chip
    size="small"
    label={
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.state.retrying"
        defaultText="Retrying"
        variant="Poppins/Body-sm"
        color="#8A5A12"
      />
    }
    sx={{
      backgroundColor: '#FDE9C8',
      borderRadius: 50,
      height: 'auto',
      py: 0.35,
      '& .MuiChip-label': { px: 1.25 },
    }}
  />
);

interface PassHeroCardProps {
  /**
   * Nullable on the wire, so treat "unknown" as its own case rather than
   * guessing a state the backend did not give us.
   */
  state?: UserPassState | null;
  /** End of the paid period. Drives both the countdown and the access date. */
  endAt?: string | null;
  /**
   * The hold is alive but no period is, because a renewal charge has not
   * landed. Suppresses the countdown and the green Active chip: both would sit
   * directly under a notice saying the card could not be charged.
   */
  paymentIssue?: boolean;
}

/**
 * The gradient card at the top of the pass screen.
 *
 * An active pass counts down to the end of the paid period; a cancelled or
 * ended one drops the countdown and says how long access lasts instead —
 * returning the last charger no longer ends a cancelled pass early, so that
 * date is accurate.
 *
 * A pass with no state still has a meaningful `endAt`, so it keeps the
 * countdown but shows no status chip — labelling it "Active" would be
 * inventing a verdict the backend withheld.
 */
export const PassHeroCard: FC<PassHeroCardProps> = ({
  state,
  endAt,
  paymentIssue = false,
}) => {
  const { locale } = useLanguageContext();

  const isFinished =
    state === UserPassState.CANCELED || state === UserPassState.ENDED;
  // A cancelled pass outranks a payment problem: cancelling stops the renewal,
  // so there is no charge being retried and saying otherwise would be a lie.
  const showProblem = paymentIssue && !isFinished;
  const remaining = getRemainingTime(endAt);
  const showCountdown = !isFinished && !showProblem && !!remaining;

  const formattedEndAt = endAt
    ? new Date(endAt).toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2,
        p: 2.5,
        color: '#FFFFFF',
        background:
          GRADIENTS[
            isFinished ? 'inactive' : showProblem ? 'problem' : 'active'
          ],
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <Stack gap={0.5} flex={1}>
          <LocalizationTypography
            textId={
              showCountdown
                ? 'app.rentalPage.newStepThree.pass.timeRemaining'
                : 'app.rentalPage.newStepThree.pass.passStatus'
            }
            defaultText={showCountdown ? 'TIME REMAINING' : 'PASS STATUS'}
            variant="Poppins/Body-sm"
            sx={{ letterSpacing: '0.08em', opacity: 0.85 }}
          />

          {showProblem ? (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.renewalPendingTitle"
              defaultText="Renewal pending"
              variant="Poppins/Heading-4"
            />
          ) : showCountdown ? (
            <Typography variant="Poppins/Heading-2">
              {formatRemaining(remaining)}
            </Typography>
          ) : state ? (
            <LocalizationTypography
              textId={`app.rentalPage.newStepThree.pass.state.${state.toLowerCase()}`}
              defaultText={PASS_STATE_LABELS[state]}
              variant="Poppins/Heading-2"
            />
          ) : (
            <LocalizationTypography
              textId="app.rentalPage.newStepThree.pass.title"
              defaultText="Rental Pass"
              variant="Poppins/Heading-2"
            />
          )}
        </Stack>

        {showProblem ? (
          <RetryingChip />
        ) : (
          state && <PassStateChip state={state} />
        )}
      </Stack>

      {/* No date while a charge is being retried — there is no settled one to
          quote, and inventing it is the whole bug this variant exists to fix. */}
      {showProblem ? (
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.renewalRetry"
          defaultText="We’ll retry the charge on your card."
          variant="Poppins/Body-sm"
          sx={{ mt: 1, opacity: 0.85 }}
        />
      ) : (
        formattedEndAt && (
          <LocalizationTypography
            textId={
              isFinished
                ? 'app.rentalPage.newStepThree.pass.accessUntil'
                : 'app.rentalPage.newStepThree.pass.expiresAt'
            }
            defaultText={
              isFinished ? 'Access until {{date}}' : 'Expires {{date}}'
            }
            textValues={{ date: formattedEndAt }}
            variant="Poppins/Subheading-md"
            sx={{ mt: 1.5 }}
          />
        )
      )}
    </Box>
  );
};
